import { useRef, useState, useEffect, type ReactNode } from 'react';

interface ColDef {
  key: string;
  label: string;
  section?: string;
}

export interface ColumnToggleGroup {
  key: string;
  label: string;
  columnKeys?: string[];
  children?: ColumnToggleGroup[];
  defaultExpanded?: boolean;
}

interface ViewConfig {
  currentViewName: string;
  isSystemView: boolean;
  onSave: () => void;
  onSaveAs: () => void;
}

interface ColumnToggleProps {
  cols: ColDef[];
  hiddenKeys: Set<string>;
  onToggle: (key: string) => void;
  onReset: () => void;
  viewConfig?: ViewConfig;
  hierarchy?: ColumnToggleGroup[];
}

export function ColumnToggle({ cols, hiddenKeys, onToggle, onReset, viewConfig, hierarchy }: ColumnToggleProps) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; right: number } | null>(null);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(() => new Set([cols[0]?.section ?? '未分组']));
  const [expandedHierarchy, setExpandedHierarchy] = useState<Set<string>>(() => new Set());
  const hiddenCount = cols.filter(col => hiddenKeys.has(col.key)).length;
  const sections = cols.reduce<Array<{ name: string; cols: ColDef[] }>>((result, col) => {
    const name = col.section ?? '未分组';
    const section = result.find(item => item.name === name);
    if (section) section.cols.push(col); else result.push({ name, cols: [col] });
    return result;
  }, []);
  const hierarchyKeys = (node: ColumnToggleGroup): string[] => [...(node.columnKeys ?? []), ...(node.children?.flatMap(hierarchyKeys) ?? [])].filter(key => cols.some(col => col.key === key));

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (popoverRef.current && !popoverRef.current.contains(event.target as Node) && btnRef.current && !btnRef.current.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  useEffect(() => {
    if (!hierarchy) return;
    const defaults = new Set<string>();
    const visit = (node: ColumnToggleGroup) => {
      if (node.defaultExpanded) defaults.add(node.key);
      node.children?.forEach(visit);
    };
    hierarchy.forEach(visit);
    setExpandedHierarchy(defaults);
  }, [hierarchy]);

  const handleClick = () => {
    if (!open && btnRef.current) {
      const rect = btnRef.current.getBoundingClientRect();
      setPos({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
    }
    setOpen(value => !value);
  };

  const showAll = () => cols.filter(col => hiddenKeys.has(col.key)).forEach(col => onToggle(col.key));
  const toggleSection = (sectionCols: ColDef[]) => {
    const allVisible = sectionCols.every(col => !hiddenKeys.has(col.key));
    sectionCols.filter(col => allVisible ? !hiddenKeys.has(col.key) : hiddenKeys.has(col.key)).forEach(col => onToggle(col.key));
  };
  const toggleExpanded = (section: string) => setExpandedSections(current => {
    const next = new Set(current);
    if (next.has(section)) next.delete(section); else next.add(section);
    return next;
  });
  const toggleHierarchyExpanded = (key: string) => setExpandedHierarchy(current => {
    const next = new Set(current);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });
  const toggleHierarchyGroup = (node: ColumnToggleGroup) => {
    const keys = hierarchyKeys(node);
    const allVisible = keys.length > 0 && keys.every(key => !hiddenKeys.has(key));
    keys.filter(key => allVisible ? !hiddenKeys.has(key) : hiddenKeys.has(key)).forEach(onToggle);
  };
  const renderHierarchyNode = (node: ColumnToggleGroup, depth: number): ReactNode => {
    const keys = hierarchyKeys(node);
    if (keys.length === 0) return null;
    const visibleCount = keys.filter(key => !hiddenKeys.has(key)).length;
    const allVisible = visibleCount === keys.length;
    const someVisible = visibleCount > 0 && !allVisible;
    const expanded = expandedHierarchy.has(node.key);
    const hasChildren = Boolean(node.children?.length);
    return <div key={node.key}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, padding: `0 12px 0 ${12 + depth * 18}px`, color: 'var(--text)', fontSize: 12, fontWeight: depth === 0 ? 700 : 600, background: depth === 0 ? 'var(--surface)' : expanded ? 'var(--primary-bg)' : 'transparent' }}>
        <button type="button" onClick={() => hasChildren && toggleHierarchyExpanded(node.key)} aria-label={`${expanded ? '收起' : '展开'}${node.label}`} disabled={!hasChildren} style={{ display: 'inline-flex', width: 16, height: 16, padding: 0, alignItems: 'center', justifyContent: 'center', border: 0, background: 'transparent', color: 'var(--text3)', cursor: hasChildren ? 'pointer' : 'default', opacity: hasChildren ? 1 : 0 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points={expanded ? '6 9 12 15 18 9' : '9 18 15 12 9 6'} /></svg>
        </button>
        <input type="checkbox" checked={allVisible} ref={element => { if (element) element.indeterminate = someVisible; }} onChange={() => toggleHierarchyGroup(node)} style={{ width: 14, height: 14, margin: 0, accentColor: 'var(--primary)', cursor: 'pointer' }} />
        <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{node.label}</span>
        <span style={{ color: 'var(--text3)', fontSize: 11, fontWeight: 500 }}>{visibleCount}/{keys.length}</span>
      </div>
      {expanded && node.children?.map(child => renderHierarchyNode(child, depth + 1))}
    </div>;
  };

  return (
    <>
      <button ref={btnRef} className="btn btn-ghost btn-sm" onClick={handleClick} title="显示/隐藏列" style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 8px', color: open ? 'var(--primary)' : 'var(--text3)', border: open ? '1px solid var(--primary)' : '1px solid var(--border)', borderRadius: 6, flexShrink: 0 }}>
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="7" height="18" rx="1"/><rect x="14" y="3" width="7" height="18" rx="1"/></svg>
        {hiddenCount > 0 && <span style={{ fontSize: 10, background: 'var(--primary)', color: '#fff', borderRadius: 8, padding: '0 5px', lineHeight: '16px', minWidth: 16, textAlign: 'center' }}>{hiddenCount}</span>}
      </button>
      {open && pos && (
        <div ref={popoverRef} style={{ position: 'fixed', top: pos.top, right: pos.right, zIndex: 1000, background: 'var(--surface)', border: '1px solid var(--border2)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,.18)', minWidth: viewConfig ? 292 : 160, maxWidth: 'calc(100vw - 16px)', maxHeight: 'min(70vh, 560px)', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
          {viewConfig ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 12px 7px', borderBottom: '1px solid var(--border2)' }}>
                <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)' }}>显示列</span>
                <button type="button" onClick={showAll} style={{ border: 0, background: 'transparent', padding: 0, color: 'var(--primary)', fontSize: 12, cursor: 'pointer' }}>全选</button>
              </div>
              <div style={{ padding: '7px 12px', color: 'var(--text3)', fontSize: 11, lineHeight: '16px', borderBottom: '1px solid var(--border)' }}>当前视图｜{viewConfig.currentViewName} · 下次进入将自动恢复</div>
              <div style={{ overflowY: 'auto', padding: '4px 0', minHeight: 0, flex: '1 1 auto' }}>
                {hierarchy ? hierarchy.map(node => renderHierarchyNode(node, 0)) : sections.map(section => {
                  const visibleCount = section.cols.filter(col => !hiddenKeys.has(col.key)).length;
                  const allVisible = visibleCount === section.cols.length;
                  const someVisible = visibleCount > 0 && !allVisible;
                  const expanded = expandedSections.has(section.name);
                  return (
                    <div key={section.name}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, minHeight: 34, padding: '0 12px', color: 'var(--text)', fontSize: 12, fontWeight: 700 }}>
                        <button type="button" onClick={() => toggleExpanded(section.name)} aria-label={`${expanded ? '收起' : '展开'}${section.name}`} style={{ display: 'inline-flex', width: 16, height: 16, padding: 0, alignItems: 'center', justifyContent: 'center', border: 0, background: 'transparent', color: 'var(--text3)', cursor: 'pointer' }}>
                          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><polyline points={expanded ? '6 9 12 15 18 9' : '9 18 15 12 9 6'} /></svg>
                        </button>
                        <input type="checkbox" checked={allVisible} ref={element => { if (element) element.indeterminate = someVisible; }} onChange={() => toggleSection(section.cols)} style={{ width: 14, height: 14, margin: 0, accentColor: 'var(--primary)', cursor: 'pointer' }} />
                        <span style={{ display: 'inline-flex', width: 14, height: 14, alignItems: 'center', justifyContent: 'center', borderRadius: 3, background: 'var(--primary)', color: '#fff', fontSize: 10 }}>−</span>
                        <span style={{ flex: 1 }}>{section.name}</span><span style={{ color: 'var(--text3)', fontSize: 11, fontWeight: 500 }}>{visibleCount}/{section.cols.length}</span>
                      </div>
                      {expanded && section.cols.map(col => {
                        const checked = !hiddenKeys.has(col.key);
                        return <label key={col.key} style={{ display: 'flex', alignItems: 'center', gap: 8, minHeight: 30, padding: '0 14px 0 44px', cursor: 'pointer', color: checked ? 'var(--text2)' : 'var(--text3)', fontSize: 12 }}><input type="checkbox" checked={checked} onChange={() => onToggle(col.key)} style={{ width: 14, height: 14, margin: 0, accentColor: 'var(--primary)', cursor: 'pointer' }} /><span>{col.label}</span></label>;
                      })}
                    </div>
                  );
                })}
                <div style={{ marginTop: 4, padding: '8px 12px 10px', borderTop: '1px solid var(--border2)', background: 'var(--surface)' }}>
                  <button type="button" className="btn btn-ghost btn-sm" onClick={onReset} style={{ width: '100%', justifyContent: 'flex-start', paddingLeft: 0, color: 'var(--text2)' }}>恢复系统默认</button>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginTop: 8 }}><button type="button" className="btn btn-outline btn-sm" onClick={viewConfig.onSaveAs}>另存为视图</button><button type="button" className="btn btn-primary btn-sm" disabled={viewConfig.isSystemView} onClick={viewConfig.onSave}>保存</button></div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div style={{ padding: '2px 12px 6px', fontSize: 11, fontWeight: 700, color: 'var(--text3)', borderBottom: '1px solid var(--border2)', marginBottom: 4 }}>显示列</div>
              <div style={{ overflowY: 'auto', padding: '0 4px 8px' }}>{cols.map((col, index) => {
                const checked = !hiddenKeys.has(col.key);
                const previousSection = index > 0 ? cols[index - 1]?.section : undefined;
                const showSection = !!col.section && col.section !== previousSection;
                return <div key={col.key}>{showSection && <div style={{ padding: index === 0 ? '6px 12px 4px' : '10px 12px 4px', fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>{col.section}</div>}<label style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 12px', cursor: 'pointer', borderRadius: 6 }}><input type="checkbox" checked={checked} onChange={() => onToggle(col.key)} style={{ width: 14, height: 14, accentColor: 'var(--primary)', cursor: 'pointer', flexShrink: 0 }} /><span style={{ fontSize: 13, color: checked ? 'var(--text)' : 'var(--text3)', userSelect: 'none' }}>{col.label}</span></label></div>;
              })}</div>
              {hiddenCount > 0 && <div style={{ padding: '6px 12px 2px', borderTop: '1px solid var(--border2)', marginTop: 4 }}><button className="btn btn-ghost btn-sm" style={{ width: '100%', fontSize: 12, color: 'var(--text2)' }} onClick={onReset}>重置全部显示</button></div>}
            </>
          )}
        </div>
      )}
    </>
  );
}
